import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiXMark, HiCheckCircle } from 'react-icons/hi2';
import { updateAudit } from '../../services/productAuditService';
import { toast } from 'react-toastify';
import { Timestamp } from 'firebase/firestore';

const PROJECT_STAGES = [
  'Idea Validation',
  'Market Research',
  'Prototype Development',
  'Product Development',
  'MVP Launch',
];

const EditAuditForm = ({ isOpen, onClose, audit, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    productName: '',
    projectStage: '',
    auditDate: '',
    findings: '',
    gaps: '',
    risks: '',
    improvementAreas: '',
    recommendations: '',
  });

  useEffect(() => {
    if (audit) {
      const auditDate = audit.auditDate?.toDate
        ? audit.auditDate.toDate().toISOString().split('T')[0]
        : audit.auditDate
          ? new Date(audit.auditDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0];

      setFormData({
        productName: audit.submissionTitle || audit.productName || '',
        submissionTitle: audit.submissionTitle || audit.productName || '',
        projectStage: audit.projectStage || '',
        auditDate: auditDate,
        findings: audit.findings || '',
        gaps: audit.gaps || '',
        risks: audit.risks || '',
        improvementAreas: audit.improvementAreas || '',
        recommendations: audit.recommendations || '',
      });
    }
  }, [audit]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!audit?.id) return;

    setLoading(true);
    try {
      await updateAudit(audit.id, formData);
      toast.success('Audit updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update audit');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !audit) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-black">Edit Product Audit</h2>
            {audit?.submissionTitle && (
              <p className="text-sm text-gray-600">
                {audit.submissionTitle} • {audit.organization || 'N/A'}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter product name"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Project Stage <span className="text-red-500">*</span>
              </label>
              <select
                name="projectStage"
                value={formData.projectStage}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">Select stage</option>
                {PROJECT_STAGES.map(stage => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Audit Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="auditDate"
                value={formData.auditDate}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Findings</label>
              <textarea
                name="findings"
                value={formData.findings}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter audit findings"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Gaps</label>
              <textarea
                name="gaps"
                value={formData.gaps}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter identified gaps"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Risks</label>
              <textarea
                name="risks"
                value={formData.risks}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter identified risks"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Improvement Areas
              </label>
              <textarea
                name="improvementAreas"
                value={formData.improvementAreas}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter areas for improvement"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Recommendations
              </label>
              <textarea
                name="recommendations"
                value={formData.recommendations}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter recommendations"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Updating...
                </>
              ) : (
                <>
                  <HiCheckCircle className="h-5 w-5" />
                  Update Audit
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditAuditForm;
